import { IconElement } from "@tvg/fuse/icon-element";
import { IconExpandMore } from "@tvg/fuse/icon/all";
import classNames from "classnames";
import { useMainState } from "client/state/Main";
import React, { useEffect, useRef, useState } from "react";
import {
  departmentHasSections,
  enterKey,
  escKey,
  getNotEmptyCategories,
  sanitizeNavDepartmentName,
  sanitizeSubmenuId,
  spaceKey,
  splitDepartmentName,
} from "./helpers/helpers";
import styles from "./NavigationBar.module.css";
import Submenu from "./submenu/Submenu";

interface NavigationProps {
  brand: string;
}

interface Department {
  name: string;
  link: string;
  splits: { name: string; items: { name: string; link: string }[] }[];
}

const NavigationBar: React.FC<NavigationProps> = () => {
  const navigation = useMainState((state) => state.navigation);
  const preventSSRSubMenu = useMainState((state) => state.preventSSRSubMenu);
  const [activeCategory, setActiveCategory] = useState<string>("");
  const [hoveredSubmenu, setHoveredSubmenu] = useState<string>("");
  const [timeoutState, setTimeoutState] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  // Escape key closes menus
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (escKey(e)) {
        setActiveCategory("");
        setHoveredSubmenu("");
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Click outside closes menus
  useEffect(() => {
    const navRoot = document.getElementById("department-navigation");
    const handleClickOutside = (e: MouseEvent) => {
      if (navRoot && !navRoot.contains(e.target as Node)) {
        setActiveCategory("");
        setHoveredSubmenu("");
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleOnMouseEnter = (name: string) => {
    if (timeoutState) {
      clearTimeout(timeoutState);
    }

    if (activeCategory === "") {
      setTimeoutState(setTimeout(() => setActiveCategory(name), 200));
    } else {
      setActiveCategory(name);
    }
  };
  const handleOnMouseLeave = () => {
    if (timeoutState) {
      clearTimeout(timeoutState);
    }
    setActiveCategory("");
  };

  if (!navigation?.departments?.length) return null;

  return (
    <nav
      className={`${styles.navigationBar} navi-header-fe-nav`}
      aria-label="Department navigation"
      data-testid="complexHeader_navBar__container"
      id="complexHeader_navBar__container"
    >
      <ul
        className={`${styles.navigationBar__list} nav__department-navigation-list`}
        aria-label="Department navigation"
        id="department-navigation"
      >
        {navigation.departments.map((department: Department, index: number) => {
          const { name, link } = department;
          const [nameLine1, nameLine2] = splitDepartmentName(name);

          const submenuButtonName = sanitizeNavDepartmentName(
            "btn-open-submenu-",
            name,
          );
          const submenuId = sanitizeSubmenuId(name);

          const showSeparator =
            index > 0 &&
            index < navigation.departments.length &&
            navigation.departments[index - 1]?.name !== "Sale";

          const notEmptyCategories = getNotEmptyCategories(department.splits);
          const hasSections = departmentHasSections(
            department,
            notEmptyCategories,
          );

          const isActive = activeCategory === name || hoveredSubmenu === name;

          const handleToggleSubmenu = (
            e: React.KeyboardEvent | React.MouseEvent,
          ) => {
            if ("key" in e && (spaceKey(e) || enterKey(e))) {
              e.preventDefault();
              setActiveCategory(isActive ? "" : name);
            }
            if (e.type === "click") {
              setActiveCategory(isActive ? "" : name);
            }
          };

          const buttonRef = useRef<HTMLButtonElement>(null);

          return (
            <li
              key={`nav-${name}`}
              className={classNames(
                `${styles.navigationBar__item} nav__department-navigation-list-item`,
                { [styles["navigationBar__item--sale"]]: name === "Sale" },
              )}
              onMouseEnter={() => handleOnMouseEnter(name)}
              onMouseLeave={handleOnMouseLeave}
            >
              <div
                className={`${styles.navigationBar__ctaWrapper} nav__cta-wrapper`}
              >
                <a
                  className={classNames(
                    `${styles.navigationBar__link} nav__department-navigation-link`,
                    {
                      [styles["navigationBar__link--sale"]]: name === "Sale",
                      [styles["navigationBar__link--separator"]]: showSeparator,
                    },
                  )}
                  href={link}
                >
                  <span
                    className={`${styles.navigationBar__linkSpan} nav__link-span`}
                  >
                    {nameLine1}
                  </span>
                  {nameLine2 && (
                    <span
                      className={`${styles.navigationBar__linkSpan} nav__link-span`}
                    >
                      {nameLine2}
                    </span>
                  )}
                </a>

                {hasSections && (
                  <button
                    className={`${styles.navigationBar__subCategoryButton} nav__department-navigation-submenu-button`}
                    id={submenuButtonName}
                    ref={buttonRef}
                    aria-controls={submenuId}
                    aria-expanded={isActive}
                    aria-label={isActive ? "Close submenu" : "Open submenu"}
                    tabIndex={0}
                    data-testid={submenuButtonName}
                    onKeyDown={handleToggleSubmenu}
                    onClick={handleToggleSubmenu}
                  >
                    <IconElement
                      className={`${styles.navigationBar__subCategoryIcon} nav__subcategory-icon`}
                      color="var(--color-brand-mid)"
                      icon={<IconExpandMore />}
                      height="var(--spacing-em16)"
                      width="var(--spacing-em16)"
                    />
                  </button>
                )}
              </div>

              {preventSSRSubMenu && hasSections && isActive ? (
                <>
                  <Submenu
                    splits={notEmptyCategories}
                    ariaLabelledBy={submenuButtonName}
                    departmentUrl={department.link}
                    departmentName={department.name}
                    id={submenuId}
                    ariaHidden={!isActive}
                    showSubmenu={isActive}
                    onSubmenuEnter={() => {}}
                    onSubmenuLeave={handleOnMouseLeave}
                    parentButtonRef={buttonRef}
                  />

                  <div
                    className={`${styles.navigationBar__overlay} nav__department-navigation-overlay`}
                    data-testid="submenu-overlay"
                    onMouseEnter={handleOnMouseLeave}
                  />
                </>
              ) : (
                !preventSSRSubMenu &&
                hasSections && (
                  <>
                    <Submenu
                      splits={notEmptyCategories}
                      ariaLabelledBy={submenuButtonName}
                      departmentUrl={department.link}
                      departmentName={department.name}
                      id={submenuId}
                      ariaHidden={!isActive}
                      showSubmenu={isActive}
                      onSubmenuEnter={() => {}}
                      onSubmenuLeave={handleOnMouseLeave}
                      parentButtonRef={buttonRef}
                    />
                    {isActive && (
                      <div
                        className={`${styles.navigationBar__overlay} nav__department-navigation-overlay`}
                        data-testid="submenu-overlay"
                        onMouseEnter={handleOnMouseLeave}
                      />
                    )}
                  </>
                )
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default React.memo(NavigationBar);
